import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { ONE_NIGHT_CENTRE_CARDS, Teams } from '@ringmaster/shared';
import type { GameId, RoleId, Team } from '@ringmaster/shared';
import { BackPill } from './BackPill.js';
import { Glyph } from './Glyph.js';
import { HoloRoleCard } from './HoloRoleCard.js';
import { roleCardBackFor, roleCardDefForCopy } from './roleCardDefs.js';
import { poolForGame, splitPoolByTeam } from './rolePool.js';
import { ONE_NIGHT_ROLE_GUIDE } from '../content/oneNightRoleGuide.js';
import { gameById } from '../assets/manifest.js';

/**
 * RoleGalleryOverlay — the full-screen deck: every card this table would be
 * dealt, face-up, at full HoloRoleCard quality, split into the two teams that
 * are about to fight over it.
 *
 * TWO SURFACES, ONE SCREEN:
 *  - the LOBBY "View roles" button, where there is no deck yet, so the deck is
 *    DERIVED from the seated count via the game's own distribution
 *    (`poolForGame`); most Activity sessions are one player waiting for friends,
 *    and this is the thing they can read while they wait;
 *  - IN GAME, the persistent roles button (ui/OneNightRolesButton.tsx), which
 *    passes the REAL dealt `pool` — mid-game that pool is already public and
 *    authoritative, so it is never re-derived. It replaced a small anchored
 *    popover that listed the same facts as text.
 *
 * Duplicates are CARDS, not badges: a deck with two Werewolves shows two
 * Werewolf cards side by side. Counting the pack is the deduction this game
 * runs on, and a "×2" chip made it something to read rather than something to
 * see.
 *
 * Reuse, not a fork:
 *  - the shell (fixed inset-0, zIndex 200, opaque #080808, scrolling) is
 *    ui/GameSelectScreen.tsx's — the other fullscreen lobby surface;
 *  - the cards are {@link HoloRoleCard} untouched (it is frozen), fed by
 *    `roleCardDefFor`/`roleCardBackFor` so a role wears the same face and copy
 *    here as it does at DEAL;
 *  - the deck order is ROLE_PLAY_ORDER, the teams are shared's `teamOf`;
 *  - the swap-order chips ({@link ONE_NIGHT_ROLE_GUIDE}) ride on the Thief and
 *    Joker cards themselves.
 *
 * The old popover's role-by-role reference list was carried here at first and
 * then cut: each card already prints its own power, so the list said everything
 * a second time. Only its closing line survives, as the footnote.
 *
 * Nothing here reads another player's secret — the deck is public.
 */
/**
 * What each side is trying to do, per game — the line under a team heading.
 *
 * PER GAME, because the two games' win rules genuinely differ and one shared
 * sentence would be a lie in one of them: One Night is decided by a SINGLE vote
 * (any wolf voted out = village), while Blood Moon runs until a side is gone
 * (every wolf dead = village; wolves reaching parity = wolves). Written as two
 * halves of one coin so reading both tells a first-timer the whole game.
 *
 * The Whisper has no line: its Deceiver hunt is not a "kill the wolf" shape at
 * all, it is retired behind `offered:false`, and inventing copy for a game
 * nobody can start is how stale strings get shipped.
 */
const WIN_GOAL: Partial<Record<GameId, Record<Team, string>>> = {
  onenight: {
    [Teams.VILLAGE]: 'Win if the table votes out a werewolf.',
    [Teams.WOLVES]: 'Win if the whole pack survives the vote.',
  },
  bloodmoon: {
    [Teams.VILLAGE]: 'Win when every werewolf is gone.',
    [Teams.WOLVES]: 'Win when the wolves match the villagers.',
  },
};

export function RoleGalleryOverlay({
  gameId,
  onClose,
  playerCount = 0,
  pool: poolProp,
  centreCount,
}: {
  gameId: GameId;
  onClose: () => void;
  /** Lobby only: seats filled right now. Clamped into the game's band. */
  playerCount?: number;
  /** In game: the REAL dealt pool. Wins over the derived one whenever present. */
  pool?: RoleId[];
  /** In game: how many of those cards are in the centre (One Night). */
  centreCount?: number;
}) {
  // The deck turns itself over: every card mounts face DOWN and flips face UP
  // together, one frame later. Nothing to tap — the flip is the screen's
  // entrance, not a puzzle. (A tap-to-flip version was built first and cut: on
  // a rules reference you consult mid-argument, making someone turn nine cards
  // over by hand is friction dressed as ceremony.)
  //
  // The one-frame delay is load-bearing. Mounting revealed and setting it again
  // would give React nothing to transition BETWEEN, so the cards would simply
  // appear; the browser needs to paint the backs once for the 3D flip to run.
  // HoloRoleCard suppresses the motion itself under prefers-reduced-motion, so
  // the reduced-motion path lands on the same final frame with no spin.
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(t);
  }, []);
  // Escape closes. Deferred like the old popover's handler so the keypress that
  // opened this can't immediately close it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const t = setTimeout(() => document.addEventListener('keydown', onKey), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const pool = poolProp && poolProp.length > 0 ? poolProp : poolForGame(gameId, playerCount);
  const teams = splitPoolByTeam(pool);
  const back = roleCardBackFor(gameId);
  const gameName = gameById(gameId)?.name ?? 'this game';
  const isOneNight = gameId === 'onenight';
  // Only One Night has an ordering fact worth printing on a card — when the two
  // swaps SETTLE. Every other game's night order is the order it deals in.
  const orderByRole = new Map(ONE_NIGHT_ROLE_GUIDE.map((e) => [e.role, e.order]));

  const subtitle = isOneNight
    ? rolesDealtTitle(pool.length, centreCount ?? ONE_NIGHT_CENTRE_CARDS)
    : `Every card ${gameName} deals at a ${pool.length}-player table.`;


  function row(team: Team) {
    const roles = teams[team];
    if (roles.length === 0) return null;
    const village = team === Teams.VILLAGE;
    return (
      <section>
        <h2 style={{ ...rowHeading, color: village ? 'var(--faction-village)' : 'var(--faction-wolf)' }}>
          <Glyph name={village ? 'candle' : 'wolfHead'} tone={village ? 'gold' : 'blood'} size={16} />
          {village ? 'Team Village' : 'Team Werewolf'}
        </h2>
        {/* The whole win rule, stated as the two halves of one coin — the vote
            either catches a wolf or it doesn't, and that is the entire game.
            Deliberately symmetrical: read together, a player who has never
            played knows what both sides are trying to do. */}
        {WIN_GOAL[gameId]?.[team] && <p style={rowGoal}>{WIN_GOAL[gameId]![team]}</p>}
        <div style={rowGrid}>
          {roles.map((role, i) => {
            // Which copy of THIS role this is, not which cell in the row — One
            // Night's two Werewolves wear different faces, and the row also
            // holds unrelated roles between them.
            const copyIndex = roles.slice(0, i).filter((r) => r === role).length;
            const order = isOneNight ? orderByRole.get(role) : undefined;
            return (
              // Duplicates are real cards, so the key carries the copy index.
              <div key={`${role}-${i}`} style={cellStyle}>
                {/* Controlled: every card in the overlay reads the SAME flag, so
                    the whole deck turns over as one gesture. */}
                <HoloRoleCard
                  def={roleCardDefForCopy(gameId, role, copyIndex)}
                  backSrc={back}
                  revealed={revealed}
                  width="100%"
                />
                {/* The chip sits OUTSIDE the card — HoloRoleCard's face is a
                    frozen composition, and this is table information about when
                    a swap settles, not part of the card art. */}
                {order && <span style={orderChip}>{order}</span>}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <div
      className="rm-role-gallery"
      role="dialog"
      aria-modal="true"
      aria-label={`Roles in ${gameName}`}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: '#080808', overflowY: 'auto' }}
    >
      <BackPill
        label="Back"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 'max(16px, env(safe-area-inset-top, 0px))',
          left: 'max(16px, env(safe-area-inset-left, 0px))',
          zIndex: 1,
        }}
      />

      <div className="rmrg-body" style={bodyStyle}>
        <header style={{ textAlign: 'center' }}>
          <h1 style={titleStyle}>The cast</h1>
          <p style={subtitleStyle}>{subtitle}</p>
        </header>

        {row(Teams.VILLAGE)}

        {/* The spine of the screen: not a heading, a confrontation. */}
        <div style={vsRow} aria-hidden>
          <span style={vsRule} />
          <span style={vsMark}>VS</span>
          <span style={vsRule} />
        </div>

        {row(Teams.WOLVES)}

        {/* The cards carry every role's power in their own copy, and the two
            swap-order chips ride on the Thief and Joker cards themselves, so the
            old popover's role-by-role list was saying it all a second time and
            was cut. This ONE line survives it: neither a card nor a chip states
            that submission order is irrelevant — see ONE_NIGHT_SPEC.md §4a. */}
        {isOneNight && (
          <p style={footnote}>
            Swaps are applied in classic card order — the Thief&rsquo;s first, then the
            Joker&rsquo;s — no matter who acted first.
          </p>
        )}
      </div>

      <style>{`
        @keyframes rmrg-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .rm-role-gallery .rmrg-body { animation: rmrg-in 0.18s var(--ease-standard); }
        /* HoloRoleCard sets touch-action:none on .hrc-card so a drag tilts the
           card instead of scrolling the page. In a scrolling GRID of cards that
           would trap the finger — the cards cover most of the viewport, and a
           7-player One Night deck is ten of them. The card is frozen, so
           re-allow vertical panning from OUR side only: inside this overlay a
           vertical drag scrolls, a horizontal one still tilts. */
        .rm-role-gallery .hrc-card { touch-action: pan-y; }
        /* The cards here turn over on their own, one frame after mount, so the
           frozen card's "Hold to reveal" prompt is an instruction to do nothing
           — and it is painted on the backs the flip is about to hide. Hidden
           rather than relabelled: there is no action to prompt. Scoped to this
           overlay, so the deal screen still teaches press-and-hold, where
           holding IS the point (a hasty tap would show your role to the room). */
        .rm-role-gallery .hrc-hint { display: none; }
        @media (prefers-reduced-motion: reduce) {
          .rm-role-gallery .rmrg-body { animation: none; }
        }
      `}</style>
    </div>
  );
}

/**
 * The title line above the deck: `"7 roles dealt, 3 in the middle"`. Pure so it
 * can be tested and so the wording lives in exactly one place. It states the ONE
 * fact a One Night table must keep straight — `pool.length` is seats PLUS the
 * centre, so the number is always bigger than the roster, and the "3 in the
 * middle" half is what stops that reading as a bug. (Lived in
 * ui/OneNightRolesButton.tsx while that button owned a popover; it moved here
 * with the screen and is re-exported there.)
 */
export function rolesDealtTitle(poolLength: number, centreCount: number): string {
  return `${poolLength} ${poolLength === 1 ? 'role' : 'roles'} dealt, ${centreCount} in the middle`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

/** "Swap settles 1st" — operational, so Inter, hairline, muted. */
export const orderChip: CSSProperties = {
  flexShrink: 0,
  fontFamily: 'var(--font-ui)',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-tertiary)',
  border: '1px solid var(--surface-stroke)',
  borderRadius: 'var(--radius-pill)',
  padding: '2px 8px',
  whiteSpace: 'nowrap',
};

const bodyStyle: CSSProperties = {
  maxWidth: 1100,
  margin: '0 auto',
  padding: 'var(--space-5)',
  paddingTop: 'max(72px, calc(env(safe-area-inset-top, 0px) + 72px))',
  paddingBottom: 'max(var(--space-6), calc(env(safe-area-inset-bottom, 0px) + var(--space-6)))',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-5)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 'clamp(26px, 6vw, 36px)',
  letterSpacing: '0.03em',
  color: 'var(--gold-5, #e6b866)',
};

const subtitleStyle: CSSProperties = {
  margin: '8px auto 0',
  maxWidth: 420,
  fontFamily: 'var(--font-ui)',
  fontSize: 14,
  lineHeight: 1.5,
  color: 'var(--text-secondary)',
};

/** The gold micro-label this app puts above a role strip — one per team row. */
const rowHeading: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  margin: '0 0 2px',
  fontFamily: 'var(--font-ui)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
};

/**
 * The team's win condition, directly under its heading. Deliberately quieter
 * than the heading (secondary text, sentence case, not tracked-out): the heading
 * names the side, this explains it, and a second all-caps line would fight the
 * cards for attention.
 */
const rowGoal: CSSProperties = {
  margin: '0 auto var(--space-3)',
  maxWidth: 420,
  fontFamily: 'var(--font-ui)',
  fontSize: 12,
  lineHeight: 1.45,
  color: 'var(--text-secondary)',
  textAlign: 'center',
};

const vsRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-4)',
  margin: 'var(--space-2) 0',
};

/** Hairlines that fade INTO the mark, so the two rows read as one collision. */
const vsRule: CSSProperties = {
  flex: 1,
  height: 1,
  background: 'linear-gradient(90deg, transparent, var(--blood-3, #802636))',
};

const vsMark: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 'clamp(22px, 6vw, 30px)',
  letterSpacing: '0.12em',
  color: 'var(--blood-5, #c22f3e)',
  textShadow: '0 0 18px rgba(153,0,0,0.55)',
};

/**
 * Auto-fit so a phone gets two cards a row and a desktop Activity gets five,
 * with no breakpoint list. Vertical scroll only — nothing here may make the
 * page scroll sideways, and a 7-player One Night deck is ten cards.
 */
/**
 * FLEX, not grid, and deliberately: a deck of 5 or 10 rarely divides evenly by
 * the column count, and CSS grid left-aligns the leftover cards of a final row
 * (`justify-content` centres the TRACKS, not a short last row). A wrapping flex
 * row centres every line, including the short one, which is what a "hand of
 * cards" has to look like.
 */
const rowGrid: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 'var(--space-4)',
};

const cellStyle: CSSProperties = {
  // The 140px floor is load-bearing for the whole point of this screen — cards
  // SIDE BY SIDE. Two cards need 2×w + gap + the body's 48px padding, so a 160px
  // card wants a 384px viewport and would leave the common 360px Android /
  // 390px iPhone Activity iframe with a single column; 140px gets two from
  // ~344px up. The card's own container query clamps its description at 12px,
  // so it stays readable at the smaller size. The 220px cap stops four cards
  // from stretching across a desktop Activity window.
  width: 'clamp(140px, 20vw, 220px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--space-2)',
};

const footnote: CSSProperties = {
  maxWidth: 520,
  margin: 'var(--space-4) auto 0',
  fontFamily: 'var(--font-ui)',
  fontSize: 12,
  lineHeight: 1.5,
  color: 'var(--text-tertiary)',
  textAlign: 'center',
};

